/**
 * §notif-center (2026-06-09) — in-app notification center client.
 *
 * The dashboard bell + toaster + history read from the tenant-scoped
 * /api/admin/notifications endpoints. This is DISTINCT from
 * ./notifications.ts, which configures operator WEBHOOK/EMAIL destinations
 * (/api/master/notifications) — different feature, different surface.
 */

import { api } from "./api";

export type NotificationSeverity = "info" | "warning" | "critical";
export type NotificationKind = "order" | "support" | "enquiry" | "system" | string;

export interface AppNotification {
  id: string;
  tenant_id: string;
  kind: NotificationKind;
  severity: NotificationSeverity;
  title: string;
  body: string;
  href: string;
  dedupe_key: string;
  metadata_json: string;
  created_at: string;   // ISO-8601
  read_at: string;      // "" when unread
  dismissed_at: string; // "" when active
}

export async function listNotifications(
  limit = 50,
): Promise<{ notifications: AppNotification[] }> {
  return api<{ notifications: AppNotification[] }>(
    `/api/admin/notifications?limit=${limit}`,
  );
}

export async function notificationsUnreadCount(): Promise<{ count: number }> {
  return api<{ count: number }>("/api/admin/notifications/unread-count");
}

export async function markAllNotificationsRead(): Promise<{ ok: boolean; updated: number }> {
  return api<{ ok: boolean; updated: number }>(
    "/api/admin/notifications/read-all",
    { method: "POST" },
  );
}

export async function markNotificationRead(id: string): Promise<{ ok: boolean }> {
  return api<{ ok: boolean }>(
    `/api/admin/notifications/${encodeURIComponent(id)}/read`,
    { method: "POST" },
  );
}

export async function dismissNotification(id: string): Promise<{ ok: boolean }> {
  return api<{ ok: boolean }>(
    `/api/admin/notifications/${encodeURIComponent(id)}/dismiss`,
    { method: "POST" },
  );
}

/** The numeric entity id stashed in metadata_json (e.g. ticket_id), or null. */
function notificationTicketId(n: AppNotification): string | number | null {
  try {
    const v = (JSON.parse(n.metadata_json || "{}") as { ticket_id?: unknown }).ticket_id;
    return v != null ? (v as string | number) : null;
  } catch {
    return null;
  }
}

/**
 * Where a notification navigates on click, or null if it has no destination
 * (e.g. a system/disk alert). Routing is derived from `kind` so it stays correct
 * per tenant; an explicit server-set `href` wins when present.
 */
export function notificationHref(n: AppNotification): string | null {
  if (n.href) return n.href;
  switch (n.kind) {
    case "order":
      return "/orders";
    case "enquiry":
      return "/enquiries";
    case "support": {
      const id = notificationTicketId(n);
      return id != null ? `/support/${id}` : "/support";
    }
    default:
      return null; // system / unknown → not navigable
  }
}
